import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserAuth } from '../../hooks/useUserAuth';

export interface AuthGuardProps {
  children: React.ReactNode;
  loginPath?: string;
  loadingComponent?: React.ReactNode;
}

/**
 * Component to protect routes requiring authentication
 * Redirects to login page if user is not authenticated
 *
 * Usage:
 * ```typescript
 * <AuthGuard>
 *   <ProtectedContent />
 * </AuthGuard>
 * ```
 *
 * With custom login path:
 * ```typescript
 * <AuthGuard loginPath="/auth/login">
 *   <ProtectedContent />
 * </AuthGuard>
 * ```
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  loginPath = '/login',
  loadingComponent,
}) => {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const { accessToken, roles, isLoading: isLoadingAuth } = useUserAuth();

  // Don't render anything on protected routes until Auth0 is initialized
  // This prevents premature redirects
  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#1c1c1c',
          color: '#ffffff',
        }}
      >
        Loading...
      </div>
    );
  }

  // Handle Auth0 errors
  if (error) {
    console.error('Auth0 error in AuthGuard:', error);
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#1c1c1c',
          color: '#ffffff',
        }}
      >
        <h2>Authentication Error</h2>
        <p>{error.message}</p>
        <button
          onClick={() => (window.location.href = loginPath)}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Return to Login
        </button>
      </div>
    );
  }

  // Show loading while fetching tokens after authentication
  if (isAuthenticated && isLoadingAuth) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#1c1c1c',
          color: '#ffffff',
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    // On the server or in production, redirect immediately
    if (typeof window !== 'undefined' && window.location.pathname !== loginPath) {
      const returnTo = window.location.pathname + window.location.search;
      window.location.href = `${loginPath}?returnTo=${encodeURIComponent(returnTo)}`;
    }
    // Prevent rendering children while redirect is happening
    return null;
  }

  // Log tokens and roles for debugging (remove in production)
  if (accessToken) {
    console.log('User authenticated with roles:', roles);
  }

  return <>{children}</>;
};
